// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/ui/stepped_carousel/SteppedCarousel.module.css';
import {
	createSteppedCarouselSnapshot,
	type SteppedCarouselMachineEvent,
	selectSteppedCarouselModel,
	transitionSteppedCarouselSnapshot,
} from '@app/features/ui/stepped_carousel/SteppedCarouselStateMachine';
import {AnimatePresence, motion, useReducedMotion} from 'framer-motion';
import type React from 'react';
import {useCallback, useLayoutEffect, useRef, useState} from 'react';

interface SteppedCarouselProps<Step extends string> {
	step: Step;
	steps: ReadonlyArray<Step>;
	children: React.ReactNode;
	direction?: number;
	focusOnStepChange?: boolean;
	ariaLabel?: string;
	ariaLive?: 'off' | 'polite';
	'data-flx'?: string;
}

const slideVariants = {
	enter: (direction: number) => ({
		opacity: 0,
		x: direction > 0 ? 24 : direction < 0 ? -24 : 0,
	}),
	center: {
		opacity: 1,
		x: 0,
	},
	exit: (direction: number) => ({
		opacity: 0,
		x: direction > 0 ? -24 : direction < 0 ? 24 : 0,
	}),
};
const slideTransition = {
	type: 'spring' as const,
	stiffness: 480,
	damping: 38,
	mass: 0.7,
};
const heightTransition = {
	type: 'spring' as const,
	stiffness: 380,
	damping: 36,
	mass: 0.7,
};
const instantTransition = {
	duration: 0,
};
const reducedMotionVariants = {
	enter: {
		opacity: 0,
		x: 0,
	},
	center: {
		opacity: 1,
		x: 0,
	},
	exit: {
		opacity: 0,
		x: 0,
	},
};
const focusableSelector = [
	'[data-step-focus="true"]',
	'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"])',
	'button:not([disabled]):not([tabindex="-1"])',
	'a[href]:not([tabindex="-1"])',
	'select:not([disabled]):not([tabindex="-1"])',
	'textarea:not([disabled]):not([tabindex="-1"])',
	'[tabindex]:not([tabindex="-1"])',
].join(',');

export function SteppedCarousel<Step extends string>({
	step,
	steps,
	children,
	direction: directionProp,
	focusOnStepChange = false,
	ariaLabel,
	ariaLive,
	'data-flx': dataFlx,
}: SteppedCarouselProps<Step>): React.ReactElement {
	const shouldReduceMotion = useReducedMotion();
	const [snapshot, setSnapshot] = useState(() =>
		createSteppedCarouselSnapshot({step, steps, direction: directionProp, focusOnStepChange}),
	);
	const model = selectSteppedCarouselModel(snapshot);
	const observerRef = useRef<ResizeObserver | null>(null);
	const paneRef = useRef<HTMLDivElement | null>(null);
	const dispatch = useCallback((event: SteppedCarouselMachineEvent) => {
		setSnapshot((current) => transitionSteppedCarouselSnapshot(current, event));
	}, []);
	const measureNode = useCallback(
		(node: HTMLElement) => {
			dispatch({
				type: 'carousel.measured',
				offsetHeight: node.offsetHeight,
				scrollHeight: node.scrollHeight,
			});
		},
		[dispatch],
	);
	const setMeasureNode = useCallback(
		(node: HTMLDivElement | null) => {
			paneRef.current = node;
			if (observerRef.current !== null) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
			if (node === null) return;
			measureNode(node);
			if (typeof ResizeObserver === 'undefined') return;
			const ro = new ResizeObserver((entries) => {
				const entry = entries[0];
				if (!entry) return;
				measureNode(node);
			});
			ro.observe(node);
			observerRef.current = ro;
		},
		[measureNode],
	);
	useLayoutEffect(() => {
		return () => {
			if (observerRef.current !== null) {
				observerRef.current.disconnect();
				observerRef.current = null;
			}
		};
	}, []);
	useLayoutEffect(() => {
		dispatch({
			type: 'carousel.propsChanged',
			step,
			steps,
			direction: directionProp,
			focusOnStepChange,
		});
	}, [directionProp, dispatch, focusOnStepChange, step, steps]);
	useLayoutEffect(() => {
		if (!focusOnStepChange || model.focusRequestId === 0) return;
		const frame = window.requestAnimationFrame(() => {
			const pane = paneRef.current;
			if (!pane) return;
			if (pane.contains(document.activeElement)) return;
			const focusTarget = pane.querySelector<HTMLElement>(focusableSelector) ?? pane;
			focusTarget.focus({preventScroll: true});
		});
		return () => window.cancelAnimationFrame(frame);
	}, [focusOnStepChange, model.focusRequestId]);
	return (
		<motion.div
			className={styles.container}
			animate={{height: model.contentHeight}}
			transition={shouldReduceMotion ? instantTransition : heightTransition}
			aria-label={ariaLabel}
			aria-live={ariaLive}
			aria-atomic={ariaLive ? true : undefined}
			role={ariaLabel ? 'group' : undefined}
			data-flx={dataFlx ?? 'ui.stepped-carousel.container'}
		>
			<AnimatePresence
				mode="wait"
				initial={false}
				custom={model.direction}
				data-flx="ui.stepped-carousel.animate-presence"
			>
				<motion.div
					key={model.step}
					ref={setMeasureNode}
					className={styles.pane}
					custom={model.direction}
					variants={shouldReduceMotion ? reducedMotionVariants : slideVariants}
					initial="enter"
					animate="center"
					exit="exit"
					transition={shouldReduceMotion ? instantTransition : slideTransition}
					tabIndex={focusOnStepChange ? -1 : undefined}
					data-flx="ui.stepped-carousel.pane"
				>
					{children}
				</motion.div>
			</AnimatePresence>
		</motion.div>
	);
}
